// Breakout panel for the admin page: how the selected day's schedule splits
// across appointment types.
//
// One measure (a count) across a handful of named categories, so it's a bar
// chart with one hue — colouring each bar differently would encode nothing and
// just invite the reader to look for meaning that isn't there. Bars are
// horizontal because the type labels are phrases, not codes, and horizontal
// labels don't need rotating. Counts are printed at the bar ends, so the chart
// is readable without hovering and without a legend (a single series is named
// by the title). The table underneath carries the same numbers plus each
// type's share, for anyone who wants the values rather than the shape.
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Heading,
  Select,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
} from "@chakra-ui/react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CANONICAL_TYPES } from "../utils/scheduleImport/normalizeType";

// Validated against the light chart surface (lightness band, chroma floor and
// >= 3:1 contrast all pass). One series, so there are no adjacent-pair or
// colour-vision-deficiency separations to worry about.
const BAR_COLOR = "#3182CE";
const GRID = "#E2E8F0";
const AXIS_INK = "#4A5568";
const LABEL_INK = "#2D3748";

const TYPE_LABELS = {
  NP: "New · Pulmonary",
  NS: "New · Sleep",
  FFP: "Follow-up · Pulmonary",
  FFS: "Follow-up · Sleep",
};

const UNSPECIFIED = "Unspecified";

// Sentinel for "don't filter" — never a real schema name, so it can't collide.
export const ALL_PROVIDERS = "__all__";

// Distinct provider schemas present in the fetched rows (tagged `_schema` by
// mergeSchemaResults — see AdminPage's fetchPatients), alphabetical. This is
// the source of truth for which providers the breakdown can be split by: it's
// whatever the Providers dropdown actually pulled, not the full clinic group,
// so a provider with nothing scheduled that day doesn't show up as a filter
// option with nothing behind it.
export function schemasIn(patients) {
  const set = new Set(
    (patients || [])
      .map((p) => p?._schema)
      .filter((s) => typeof s === "string" && s.trim())
  );
  return Array.from(set).sort();
}

export function buildTypeBreakdown(patients) {
  const counts = {};
  (patients || []).forEach((p) => {
    const code = String(p?.type || "").trim().toUpperCase();
    const key = code || UNSPECIFIED;
    counts[key] = (counts[key] || 0) + 1;
  });

  // Canonical codes in their documented order, then whatever else an import
  // left behind (normalizeAppointmentType returns an unrecognised phrase
  // untouched rather than guessing), then the blanks last.
  const extras = Object.keys(counts)
    .filter((k) => !CANONICAL_TYPES.includes(k) && k !== UNSPECIFIED)
    .sort();
  const order = [
    ...CANONICAL_TYPES.filter((t) => counts[t]),
    ...extras,
    ...(counts[UNSPECIFIED] ? [UNSPECIFIED] : []),
  ];

  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return {
    total,
    rows: order.map((code) => ({
      code,
      label: TYPE_LABELS[code] || code,
      count: counts[code],
      share: total ? counts[code] / total : 0,
    })),
  };
}

export default function PatientTypeBreakdownDrawer({
  isOpen,
  onClose,
  patients,
  selectedDate,
}) {
  const schemaOptions = useMemo(() => schemasIn(patients), [patients]);
  const [schemaFilter, setSchemaFilter] = useState(ALL_PROVIDERS);

  // Reset to "all providers" each time the drawer is opened, rather than
  // leaving whatever a previous session was scoped to — the date and provider
  // selection upstream can change while the drawer is closed, and a stale
  // filter would silently hide data instead of showing the day's full split.
  useEffect(() => {
    if (isOpen) setSchemaFilter(ALL_PROVIDERS);
  }, [isOpen]);

  const scopedPatients = useMemo(() => {
    if (schemaFilter === ALL_PROVIDERS) return patients;
    return (patients || []).filter((p) => p?._schema === schemaFilter);
  }, [patients, schemaFilter]);

  const { total, rows } = useMemo(
    () => buildTypeBreakdown(scopedPatients),
    [scopedPatients],
  );
  const busiest = useMemo(
    () => rows.reduce((best, r) => (!best || r.count > best.count ? r : best), null),
    [rows],
  );

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="md">
      <DrawerOverlay />
      <DrawerContent data-testid="patient-breakdown-drawer">
        <DrawerCloseButton />
        <DrawerHeader>
          Patients by type
          <Text fontSize="sm" fontWeight="normal" color="gray.500">
            {selectedDate || "No date selected"}
          </Text>
        </DrawerHeader>
        <DrawerBody pb={8}>
          {/* Only worth showing when the fetch actually pulled more than one
              provider's schema (see schemasIn) — a single-provider clinic has
              nothing to split by. */}
          {schemaOptions.length > 1 && (
            <FormControl mb={4}>
              <FormLabel fontSize="xs" color="gray.500" mb={1}>
                Provider
              </FormLabel>
              <Select
                size="sm"
                value={schemaFilter}
                onChange={(e) => setSchemaFilter(e.target.value)}
              >
                <option value={ALL_PROVIDERS}>All providers</option>
                {schemaOptions.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </Select>
            </FormControl>
          )}
          {total === 0 ? (
            <Text fontSize="sm" color="gray.500">
              {schemaFilter === ALL_PROVIDERS
                ? "No appointments on this date."
                : "No appointments on this date for this provider."}
            </Text>
          ) : (
            <VStack align="stretch" spacing={6}>
              <SimpleGrid columns={2} spacing={3}>
                <Stat>
                  <StatLabel fontSize="xs" color="gray.500">
                    Appointments
                  </StatLabel>
                  <StatNumber fontSize="2xl">{total}</StatNumber>
                  <StatHelpText fontSize="xs" mb={0}>
                    across {rows.length} type{rows.length === 1 ? "" : "s"}
                  </StatHelpText>
                </Stat>
                {busiest && (
                  <Stat>
                    <StatLabel fontSize="xs" color="gray.500">
                      Most common
                    </StatLabel>
                    <StatNumber fontSize="2xl">{busiest.code}</StatNumber>
                    <StatHelpText fontSize="xs" mb={0}>
                      {busiest.count} · {Math.round(busiest.share * 100)}%
                    </StatHelpText>
                  </Stat>
                )}
              </SimpleGrid>

              <Box>
                <Heading size="sm" mb={2}>
                  Appointments per type
                </Heading>
                <Box
                  bg="white"
                  p={2}
                  borderRadius="md"
                  borderWidth={1}
                  height={`${Math.max(150, rows.length * 46 + 40)}px`}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={rows}
                      layout="vertical"
                      margin={{ top: 4, right: 40, bottom: 4, left: 4 }}
                    >
                      {/* Only the value axis gets grid lines; horizontal rules
                          between categories would just be noise. */}
                      <CartesianGrid
                        horizontal={false}
                        stroke={GRID}
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        type="number"
                        allowDecimals={false}
                        tick={{ fontSize: 12, fill: AXIS_INK }}
                        stroke={GRID}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={135}
                        tick={{ fontSize: 12, fill: LABEL_INK }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        cursor={{ fill: "#EDF2F7" }}
                        formatter={(value) => [
                          `${value} appointment${value === 1 ? "" : "s"}`,
                          "Count",
                        ]}
                      />
                      <Bar
                        dataKey="count"
                        fill={BAR_COLOR}
                        barSize={18}
                        radius={[0, 4, 4, 0]}
                        isAnimationActive={false}
                      >
                        <LabelList
                          dataKey="count"
                          position="right"
                          style={{
                            fill: LABEL_INK,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Box>

              <Box>
                <Heading size="sm" mb={2}>
                  Values
                </Heading>
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Type</Th>
                      <Th isNumeric>Count</Th>
                      <Th isNumeric>Share</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {rows.map((r) => (
                      <Tr key={r.code}>
                        <Td>
                          <Text fontSize="sm" fontWeight="600">
                            {r.code}
                          </Text>
                          {TYPE_LABELS[r.code] && (
                            <Text fontSize="xs" color="gray.500">
                              {TYPE_LABELS[r.code]}
                            </Text>
                          )}
                        </Td>
                        <Td isNumeric fontSize="sm">
                          {r.count}
                        </Td>
                        <Td isNumeric fontSize="sm">
                          {Math.round(r.share * 100)}%
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
